import { styled } from '@mui/material'
import { useState, useCallback, useMemo } from 'react'
import { formatDateTimeMinute } from '../../../../../utils/date'
import { RichTextEditor } from './RichTextEditor'
import { RichTextContent } from './RichTextContent'
import { DiscussionThread as DiscussionThreadType } from '../../../../../domain/entity/DiscussionEntity'
import { intl } from '../../../../../i18n'
import { useDiscussionActions } from './hooks/useDiscussionActions'

interface DiscussionThreadProps {
  thread: DiscussionThreadType
  projectUuid: string
  versionPageUuid: string
  onRefresh: () => void
}

export const DiscussionThread = ({
  thread,
  projectUuid,
  versionPageUuid,
  onRefresh,
}: DiscussionThreadProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showReplyForm, setShowReplyForm] = useState(false)

  const { handleCreateReply } = useDiscussionActions({
    versionPageUuid,
    onRefresh,
  })

  const handleReplySubmit = useCallback(
    async (content: string) => {
      if (content.trim()) {
        await handleCreateReply(thread.threadUuid, content.trim())
        setShowReplyForm(false)
      }
    },
    [handleCreateReply, thread.threadUuid]
  )

  return (
    <ThreadItem>
      <ThreadComments
        thread={thread}
        isExpanded={isExpanded}
        onToggleExpanded={() => setIsExpanded(!isExpanded)}
        showReplyForm={showReplyForm}
        onSetShowReplyForm={setShowReplyForm}
        onReplySubmit={handleReplySubmit}
        projectUuid={projectUuid}
      />
    </ThreadItem>
  )
}

interface ThreadCommentsProps {
  thread: DiscussionThreadType
  isExpanded: boolean
  onToggleExpanded: () => void
  showReplyForm: boolean
  onSetShowReplyForm: (show: boolean) => void
  onReplySubmit: (content: string) => void
  projectUuid?: string
}

const ThreadComments = ({
  thread,
  isExpanded,
  onToggleExpanded,
  showReplyForm,
  onSetShowReplyForm,
  onReplySubmit,
  projectUuid,
}: ThreadCommentsProps) => {
  const firstComment = useMemo(
    () => thread.discussionComments[0],
    [thread.discussionComments]
  )
  const remainingComments = useMemo(
    () => thread.discussionComments.slice(1),
    [thread.discussionComments]
  )

  return (
    <>
      <CommentItem>
        <CommentAuthor>{firstComment.commentedBy}</CommentAuthor>
        <RichTextContent content={firstComment.content} />
        <CommentTime>
          {formatDateTimeMinute(firstComment.createdAt)}
          {firstComment.isEdited && (
            <EditedLabel>
              {intl.formatMessage({ id: 'discussion.edited' })}
            </EditedLabel>
          )}
        </CommentTime>
      </CommentItem>

      {remainingComments.length > 0 && (
        <RepliesToggle onClick={onToggleExpanded}>
          {isExpanded
            ? intl.formatMessage({ id: 'discussion.reply.hide' })
            : intl.formatMessage(
                { id: 'discussion.reply.show' },
                { n: remainingComments.length }
              )}
        </RepliesToggle>
      )}

      {isExpanded && (
        <>
          {remainingComments.map(comment => (
            <ReplyItem key={comment.commentUuid}>
              <CommentAuthor>{comment.commentedBy}</CommentAuthor>
              <RichTextContent content={comment.content} />
              <CommentTime>
                {formatDateTimeMinute(comment.createdAt)}
              </CommentTime>
            </ReplyItem>
          ))}
          <ReplyFormArea onSubmit={onReplySubmit} projectUuid={projectUuid} />
        </>
      )}

      {remainingComments.length === 0 &&
        (showReplyForm ? (
          <ReplyFormArea
            onSubmit={onReplySubmit}
            onCancel={() => onSetShowReplyForm(false)}
            showCancel={true}
            projectUuid={projectUuid}
          />
        ) : (
          <ReplyToggle onClick={() => onSetShowReplyForm(true)}>
            {intl.formatMessage({ id: 'discussion.reply' })}
          </ReplyToggle>
        ))}
    </>
  )
}

interface ReplyFormAreaProps {
  onSubmit: (content: string) => void
  onCancel?: () => void
  showCancel?: boolean
  projectUuid?: string
}

const ReplyFormArea = ({
  onSubmit,
  onCancel,
  showCancel,
  projectUuid,
}: ReplyFormAreaProps) => {
  return (
    <RichTextEditor
      placeholder={intl.formatMessage({ id: 'discussion.reply.placeholder' })}
      onSubmit={onSubmit}
      onCancel={onCancel}
      showCancel={showCancel}
      submitLabel={intl.formatMessage({ id: 'discussion.reply' })}
      projectUuid={projectUuid}
    />
  )
}

const ThreadItem = styled('div')({
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  padding: '12px',
  backgroundColor: '#fafafa',
})

const CommentItem = styled('div')({
  marginBottom: '8px',
})

const ReplyItem = styled('div')({
  marginLeft: '16px',
  marginBottom: '8px',
  paddingLeft: '12px',
  borderLeft: '2px solid #e0e0e0',
})

const CommentAuthor = styled('div')({
  fontSize: '12px',
  fontWeight: 600,
  color: '#000000',
  marginBottom: '4px',
})

const CommentTime = styled('div')({
  fontSize: '11px',
  color: '#666',
  textAlign: 'right',
})

const EditedLabel = styled('span')({
  fontSize: '11px',
  color: '#666',
  fontStyle: 'italic',
  marginLeft: '8px',
})

const RepliesToggle = styled('button')({
  background: 'none',
  border: 'none',
  color: '#1976d2',
  fontSize: '12px',
  cursor: 'pointer',
  padding: '4px 0',
  marginBottom: '8px',
  '&:hover': {
    textDecoration: 'underline',
  },
})

const ReplyToggle = styled('button')({
  background: 'none',
  border: 'none',
  color: '#1976d2',
  fontSize: '12px',
  cursor: 'pointer',
  padding: '4px 0',
  '&:hover': {
    textDecoration: 'underline',
  },
})
